/**
 * SetupProgressWidget — Stripe-style persistent setup checklist.
 *
 * Floats on the right side of every page while the company wizard is
 * incomplete. Clicking × minimises it to a small FAB with a badge; it
 * never fully disappears until setup_completed is true. This ensures
 * admins can always find their setup progress without hunting for it.
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Card, CardContent, Typography, LinearProgress, IconButton,
  List, ListItem, ListItemIcon, ListItemText, Button, Fab, Badge, Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { setupWizardApi } from '@/api/endpoints/setupWizard';
import { useAuthStore } from '@/stores/authStore';
import { TYPE } from '@/theme/typography';

export default function SetupProgressWidget() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const activeCompany = useAuthStore((s) => {
    const id = s.activeCompanyId ?? s.companies[0]?.company_id;
    return s.companies.find((c) => c.company_id === id);
  });

  const companyId = activeCompany?.company_id;
  const isAdmin = activeCompany !== undefined && activeCompany.role_name !== 'Staff';

  const minKey = `setup-widget-minimized:${companyId}`;
  const [minimized, setMinimized] = useState(
    () => localStorage.getItem(minKey) === '1',
  );

  const { data: status } = useQuery({
    queryKey: ['setup/status'],
    queryFn: () => setupWizardApi.getStatus(),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
    enabled: isAdmin && !!companyId,
  });

  // Hide when: not admin, setup complete, skipped, or currently on /setup page.
  if (!isAdmin || !status || status.is_complete || status.skipped) return null;
  if (pathname === '/setup') return null;

  const steps = status.steps ?? [];
  const completed = status.completed_steps ?? [];
  const requiredSteps = steps.filter((s) => s.required);
  const requiredRemaining = requiredSteps.filter((s) => !completed.includes(s.step)).length;
  const pct = steps.length > 0 ? Math.round((completed.length / steps.length) * 100) : 0;

  const handleMinimize = () => {
    setMinimized(true);
    localStorage.setItem(minKey, '1');
  };
  const handleExpand = () => {
    setMinimized(false);
    localStorage.removeItem(minKey);
  };

  // ── Minimised state — small FAB with badge ──────────────────────
  if (minimized) {
    return (
      <Tooltip title={`${requiredRemaining} required setup steps remaining — click to resume`} placement="left">
        <Badge
          badgeContent={requiredRemaining}
          color="error"
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 24,
            zIndex: (theme) => theme.zIndex.drawer - 1,
          }}
        >
          <Fab
            size="medium"
            color="primary"
            onClick={handleExpand}
            aria-label="Resume company setup"
            sx={{ width: 48, height: 48 }}
          >
            <RocketLaunchIcon sx={{ fontSize: 20 }} />
          </Fab>
        </Badge>
      </Tooltip>
    );
  }

  // ── Expanded state — checklist card ────────────────────────────
  return (
    <Card
      sx={{
        position: 'fixed',
        bottom: 96,
        right: 24,
        width: 296,
        zIndex: (theme) => theme.zIndex.drawer - 1,
        boxShadow: 8,
        borderRadius: 2,
        overflow: 'visible',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunchIcon sx={{ color: 'white', fontSize: 18 }} />
          <Typography variant="body2" fontWeight={TYPE.bold} color="white" sx={{ fontSize: TYPE.sm }}>
            {activeCompany?.company_name
              ? `Finish setting up ${activeCompany.company_name}`
              : 'Complete your setup'}
          </Typography>
        </Box>
        <Tooltip title="Minimise" placement="left">
          <IconButton
            size="small"
            onClick={handleMinimize}
            aria-label="Minimise setup checklist"
            sx={{ color: 'white', p: 0.25 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Progress bar */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {completed.length} of {steps.length} steps done
            </Typography>
            <Typography variant="caption" fontWeight={TYPE.semibold} color="primary.main">
              {pct}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 5, borderRadius: 3 }} />
        </Box>

        {/* Step list */}
        <List dense disablePadding sx={{ mb: 1.5 }}>
          {steps.map((step) => {
            const done = completed.includes(step.step);
            return (
              <ListItem
                key={step.step}
                disablePadding
                onClick={done ? undefined : () => navigate('/setup')}
                sx={{
                  py: 0.25,
                  px: 0.5,
                  borderRadius: 1,
                  opacity: done ? 0.55 : 1,
                  cursor: done ? 'default' : 'pointer',
                  '&:hover': { bgcolor: done ? 'transparent' : 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 26 }}>
                  {done
                    ? <CheckCircleIcon sx={{ fontSize: 17, color: 'success.main' }} />
                    : <RadioButtonUncheckedIcon sx={{ fontSize: 17, color: step.required ? 'warning.main' : 'text.disabled' }} />
                  }
                </ListItemIcon>
                <ListItemText
                  primary={step.label}
                  slotProps={{
                    primary: {
                      variant: 'body2',
                      sx: {
                        fontSize: TYPE.xs,
                        fontWeight: done ? TYPE.normal : TYPE.medium,
                        textDecoration: done ? 'line-through' : 'none',
                        color: done ? 'text.disabled' : 'text.primary',
                      },
                    },
                  }}
                />
                {step.required && !done && (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 10, color: 'warning.main', flexShrink: 0, ml: 0.5 }}
                  >
                    req
                  </Typography>
                )}
              </ListItem>
            );
          })}
        </List>

        <Button
          variant="contained"
          size="small"
          fullWidth
          onClick={() => navigate('/setup')}
          sx={{ textTransform: 'none', fontSize: TYPE.xs, py: 0.75 }}
        >
          {completed.length === 0 ? 'Start Setup' : 'Continue Setup'}
        </Button>
      </CardContent>
    </Card>
  );
}
